import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Send, Stars } from 'lucide-react';
import { Seo } from '../components/seo/Seo';
import { Footer } from '../components/brand/Footer';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /survey/:token — Blueprint Decision #31.
 *
 * Public, no-auth CG-CAHPS-style survey. The patient receives a link;
 * they answer 7 questions; the response feeds the aggregate stats that
 * power the payer-portal outcomes module.
 */
export default function PublicSurvey() {
  const { token } = useParams();
  const [config, setConfig] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/survey/${token}`, { timeout: 12000 });
        if (!cancelled) {
          setConfig(data);
          if (data.already_responded) setDone(true);
        }
      } catch (e) {
        if (!cancelled) setConfig({ error: e?.response?.status === 404 ? 'not_found' : 'unknown' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const setAnswer = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));

  const submit = async () => {
    const need = (config?.questions || []).filter((q) => q.kind === 'scale_10');
    const missing = need.filter((q) => answers[q.id] == null);
    if (missing.length > 0) {
      toast.error(`Please answer all ${need.length} rating questions before submitting.`);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/survey/${token}/respond`, { answers });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bg flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-cn-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (config?.error || config?.expired) {
    return (
      <div className="min-h-screen bg-cn-bg" data-testid="survey-not-available">
        <div className="cn-container py-20 text-center max-w-md">
          <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto mb-3" />
          <h1 className="font-display font-bold text-2xl text-cn-text mb-2">Survey unavailable</h1>
          <p className="text-sm text-cn-text-muted mb-6">
            {config?.expired ? 'This survey has expired.' : 'We could not find that survey link.'}
          </p>
          <Link to="/" className="cn-btn cn-btn-primary">Back to CaraLoom</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-cn-bg" data-testid="survey-done">
        <header className="border-b border-cn-border bg-cn-bone-100/40">
          <div className="cn-container py-5 flex items-center gap-3">
            <CaraLoomLogo size={28} />
          </div>
        </header>
        <section className="cn-container py-20 max-w-2xl text-center">
          <CheckCircle2 className="w-16 h-16 text-cn-primary mx-auto mb-4" />
          <h1 className="font-display font-bold text-3xl text-cn-text mb-2">Thank you</h1>
          <p className="text-cn-text-muted">Your feedback helps us improve care for every CaraLoom patient.</p>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="public-survey-page">
      <Seo title="Care quality survey | CaraLoom" description="A 90-second CG-CAHPS-style survey about your recent visit." path={`/survey/${token}`} />

      <header className="border-b border-cn-border bg-cn-bone-100/40">
        <div className="cn-container py-5 flex items-center gap-3">
          <CaraLoomLogo size={28} />
          <span className="text-cn-text-muted text-sm">Care quality survey</span>
        </div>
      </header>

      <section className="cn-section pt-10 pb-6">
        <div className="cn-container max-w-2xl">
          <div className="inline-flex items-center gap-2 cn-trust-badge mb-4" data-testid="survey-eyebrow">
            <Stars className="w-3.5 h-3.5 text-cn-primary" /> 7 questions · ~90 seconds
          </div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-cn-text leading-tight" data-testid="survey-headline">
            How was your recent visit?
          </h1>
          <p className="mt-3 text-base text-cn-text-muted leading-relaxed">
            Your honest answers help us pair you with the right care team and improve the experience for the next patient.
          </p>
        </div>
      </section>

      <section className="cn-section py-6">
        <div className="cn-container max-w-2xl">
          <ol className="space-y-6" data-testid="survey-questions">
            {(config.questions || []).map((q, idx) => (
              <li key={q.id} className="cn-card p-5" data-testid={`survey-q-${q.id}`}>
                <div className="font-display font-semibold text-cn-text mb-3">
                  <span className="text-cn-primary font-bold mr-2">{idx + 1}.</span>{q.text}
                </div>
                {q.kind === 'scale_10' ? (
                  <div className="flex flex-wrap gap-1.5" role="radiogroup">
                    {Array.from({ length: 11 }, (_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setAnswer(q.id, i)}
                        className={`w-9 h-9 rounded-lg border text-sm font-bold transition-colors ${
                          answers[q.id] === i
                            ? 'bg-cn-primary text-white border-cn-primary'
                            : 'bg-cn-bg text-cn-text border-cn-border hover:border-cn-primary/40'
                        }`}
                        data-testid={`survey-q-${q.id}-${i}`}
                        aria-label={`Rate ${i}`}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2" role="radiogroup">
                    {(q.choices || []).map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setAnswer(q.id, c)}
                        className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                          answers[q.id] === c
                            ? 'bg-cn-primary text-white border-cn-primary'
                            : 'bg-cn-bg text-cn-text border-cn-border hover:border-cn-primary/40'
                        }`}
                        data-testid={`survey-q-${q.id}-${c}`}
                      >
                        {c.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ol>

          <Button onClick={submit} disabled={submitting} className="cn-btn cn-btn-primary w-full mt-6" data-testid="survey-submit">
            <Send className="w-4 h-4" />
            {submitting ? 'Submitting…' : 'Submit feedback'}
          </Button>

          <p className="text-[11px] text-cn-text-muted text-center mt-4">
            Your responses are kept confidential and never shared with your clinician individually — they roll up to aggregate quality scores.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
